import { multiClientMiddleware } from 'redux-axios-middleware';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { createBrowserHistory } from 'history';
import { configureStore } from '@reduxjs/toolkit';
import {
  createTransform,
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import omit from 'lodash/omit';
import storage from 'redux-persist/lib/storage';
import { QueryClient } from '@tanstack/react-query';

import AxiosClient, { AdminApiClient, piggyBackrAxiosClient } from './api-client';

import rootReducer from 'reducers';
import {
  CREATE_GIFT_CARD_APPEARANCE,
  CREATE_USER_PICTURE,
  UPDATE_GIFT_CARD_APPEARANCE,
  UPDATE_USER_PICTURE,
} from 'constants/redux-actions';
import { API_GET_CURRENT_SIGN_IN_AT, API_UPDATE_TRACKING_AUTHENTICATION } from 'constants/api-paths';
import { failedWithCurrentToken, trackUserAuthentication } from 'utils/authentication';
import adminAuthActions from 'actions/admin/admin-auth-actions';
import loadingApiActions from 'actions/loading-api-actions';
import authActions from 'actions/auth-actions';
import { isMfaChallengeResponse } from 'utils/mfa';

const history = createBrowserHistory();

const persistConfig = {
  key: import.meta.env.VITE_WEBAPP_NAME,
  storage,
  whitelist: ['auth', 'adminAuth', 'designPaths'],
  // a pending challenge is a 10-minute token; it must not outlive the tab
  transforms: [createTransform((auth) => omit(auth, 'mfaChallenge'), null, { whitelist: ['auth'] })],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const axiosMiddlewareConfig = {
  interceptors: {
    request: [
      ({ getState, dispatch }, request) => {
        if (request.skipAPI) {
          const error = new Error('Request skipped');
          error.skipAPI = true;
          error.response = {
            status: 204,
            statusText: 'No Content',
            headers: {},
            data: null,
            config: request,
            skipped: true,
          };
          throw error;
        }

        const userToken = get(getState(), 'auth.token');
        const adminToken = get(getState(), 'adminAuth.token');

        const headers = {};

        if (adminToken) {
          headers['Admin-Authorization'] = `Bearer ${adminToken}`;
        }

        if (userToken) {
          headers['Authorization'] = `Bearer ${userToken}`;

          if (request.url !== API_UPDATE_TRACKING_AUTHENTICATION && request.url !== API_GET_CURRENT_SIGN_IN_AT) {
            trackUserAuthentication();
          }
        }

        const selectedLanguage = get(getState(), 'lang.selectedLang');
        if (selectedLanguage) {
          headers['Lang'] = selectedLanguage;
        }

        merge(request, { headers });

        dispatch(loadingApiActions.addLoadingApi());
        return request;
      },
    ],
    response: [
      {
        success: ({ dispatch }, response) => {
          dispatch(loadingApiActions.clearLoadedApi());
          return Promise.resolve(response);
        },
        error: ({ dispatch, getState }, error) => {
          if (error.skipAPI) {
            return Promise.resolve(error.response);
          }
          const status = get(error, 'response.status');
          // Sign out if token is expired — unless the request carried a token that has since been rotated
          if (status === 401 && failedWithCurrentToken(error, get(getState(), 'auth.token'))) {
            dispatch(authActions.clearToken());
          }
          if (status === 403 && isMfaChallengeResponse(get(error, 'response.data'))) {
            dispatch(authActions.setMfaChallenge(error.response.data));
          }

          dispatch(loadingApiActions.clearLoadedApi());
          return Promise.reject(error);
        },
      },
    ],
  },
};

const piggyBackrAxiosMiddlewareConfig = {
  interceptors: {
    request: [
      (_, request) => {
        if (request.skipAPI) {
          const error = new Error('Request skipped');
          error.skipAPI = true;
          error.response = {
            status: 204,
            statusText: 'No Content',
            headers: {},
            data: null,
            config: request,
            skipped: true,
          };
          throw error;
        }

        return request;
      },
    ],
    response: [
      {
        success: (_, response) => response,
        error: (_, error) => {
          if (error.skipAPI) {
            return Promise.resolve(error.response);
          }
          return Promise.reject(error);
        },
      },
    ],
  },
};

const multiClientMiddlewareConfig = {
  default: {
    client: AxiosClient,
    options: axiosMiddlewareConfig,
  },
  piggyBackr: {
    client: piggyBackrAxiosClient,
    options: piggyBackrAxiosMiddlewareConfig,
  },
  admin: {
    client: AdminApiClient,
    options: axiosMiddlewareConfig,
  },
};

const middleware = (getDefaultMiddleware) =>
  getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [
        FLUSH,
        REHYDRATE,
        PAUSE,
        PERSIST,
        PURGE,
        REGISTER,
        CREATE_USER_PICTURE,
        UPDATE_USER_PICTURE,
        CREATE_GIFT_CARD_APPEARANCE,
        UPDATE_GIFT_CARD_APPEARANCE,
      ],
    },
  }).concat(multiClientMiddleware(multiClientMiddlewareConfig));

const store = configureStore({
  reducer: persistedReducer,
  middleware,
  devTools: import.meta.env.MODE !== 'production',
});

// redux-axios-middleware attaches a client's interceptors on the first action that names it, so
// bind the admin client now: direct AdminApiClient calls must carry Admin-Authorization on every route.
store.dispatch(adminAuthActions.initAdminClient());

const persistor = persistStore(store);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;

        const status = get(error, ['response', 'status']);

        if (status) {
          const isClientError = status && status >= 400 && status <= 499;
          return !isClientError;
        }

        return true;
      },
      staleTime: 1000,
    },
  },
});

export { history, store, persistor, middleware, queryClient };
